import { useState, useEffect } from "react";
import FinanceMetrics from "../../components/finance/FinanceMetrics";
import PageMeta from "../../components/common/PageMeta";
import Button from "../../components/ui/button/Button";
import FinanceTable from "../../components/finance/FinanceTable";
import BankDetails from "../../components/finance/BankDetails";
import FeeDetails from "../../components/finance/FeeDetails";
import ComponentCard from "../../components/common/ComponentCard";
import { AsaasFinancialNotice } from "../../components/common/AsaasSeal";
import { useNavigate, useLocation } from "react-router-dom";
import { AlertCircle } from 'lucide-react';
import Input from "../../components/form/input/InputField";
import Modal from '../../components/common/Modal';
import Alert from '../../components/ui/alert/Alert';
import api from "../../services/api";
import authService from "../../services/authService";
import KycDocumentsCard from "../../components/kyc/KycDocumentsCard";
import { useCompanyKyc } from "../../hooks/useCompanyKyc";
import { usePermissions } from "../../hooks/usePermissions";

// Função para formatar valores monetários no padrão brasileiro
const formatCurrency = (value: string): { formattedValue: string, numericValue: number } => {
    // Remove tudo que não é número
    const numbers = value.replace(/\D/g, '');

    // Converte para número (em centavos)
    const centsValue = parseInt(numbers) || 0;

    // Converte centavos para real
    const realValue = centsValue / 100;

    // Formata no padrão brasileiro
    const formatter = new Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

    return {
        formattedValue: formatter.format(realValue),
        numericValue: realValue
    };
};

// Função para formatar valor para exibição (com R$)
const formatCurrencyDisplay = (value: number): string => {
    return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
    }).format(value);
};

export default function MyFinance() {
    const { hasPermission } = usePermissions();
    const navigate = useNavigate();
    const location = useLocation();

    // Definindo um tipo para as abas
    type TabType = "payouts" | "bank" | "fees" | "identity";

    // Estado para controlar a aba ativa
    const [activeTab, setActiveTab] = useState<TabType>("payouts");

    // Estados para o modal de saque
    const [withdrawModalOpen, setWithdrawModalOpen] = useState(false);
    const [withdrawAmount, setWithdrawAmount] = useState("");
    const [withdrawLoading, setWithdrawLoading] = useState(false);
    const [alertInfo, setAlertInfo] = useState<{ show: boolean; type: "success" | "error" | "warning"; title: string; message: string } | null>(null);

    // Estados para filtros da tabela de saques
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("");

    // Estados para armazenar os dados financeiros e da empresa
    const [isLoading, setIsLoading] = useState(false);
    const [balanceData, setBalanceData] = useState({
        available: 0,
        pending: 0,
        reserved: 0,
        total: 0
    });
    const [companyData, setCompanyData] = useState({
        id: "",
        companyId: "",
        fantasyName: "",
        legalName: "",
        documentType: "",
        documentNumber: "",
        fullAddress: "",
        street: "",
        number: "",
        complement: "",
        neighborhood: "",
        city: "",
        state: "",
        zipCode: "",
        country: "Brasil",
        phone: "",
        contactEmail: "",
        pixKey: null as string | null,
        legalRepresentative: "",
        legalRepresentativeDocument: ""
    });

    // Estado para armazenar as taxas
    const [fees, setFees] = useState([]);

    // Hook para verificar status KYC da empresa
    const { kycStatus, loading: kycLoading, refetch: refetchKyc } = useCompanyKyc(companyData.id || null);

    // Função para abrir o modal de saque
    const handleOpenWithdrawModal = () => {
        setWithdrawModalOpen(true);
    };

    // Função para fechar o modal de saque
    const handleCloseWithdrawModal = () => {
        setWithdrawModalOpen(false);
        setWithdrawAmount("");
    };

    // Função para processar o saque
    const handleWithdraw = async () => {
        try {
            // Verificar se o saldo está disponível
            if (balanceData.available <= 0) {
                setAlertInfo({
                    show: true,
                    type: "error",
                    title: "Erro",
                    message: "Saldo insuficiente para realizar saque."
                });
                return;
            }

            setWithdrawLoading(true);

            // Validações
            if (!withdrawAmount || withdrawAmount.trim() === '') {
                setAlertInfo({
                    show: true,
                    type: "error",
                    title: "Erro",
                    message: "Por favor, informe o valor do saque."
                });
                setWithdrawLoading(false);
                return;
            }

            // Converter valor formatado para número
            const numericValue = parseFloat(withdrawAmount.replace(/\./g, '').replace(',', '.'));

            if (isNaN(numericValue) || numericValue <= 0) {
                setAlertInfo({
                    show: true,
                    type: "error",
                    title: "Erro",
                    message: "Por favor, informe um valor válido."
                });
                setWithdrawLoading(false);
                return;
            }

            if (numericValue > balanceData.available) {
                setAlertInfo({
                    show: true,
                    type: "error",
                    title: "Erro",
                    message: "Valor do saque não pode ser maior que o saldo disponível."
                });
                setWithdrawLoading(false);
                return;
            }

            // Chamar API de saque (backend vai buscar a chave PIX da empresa)
            const response = await api.post('/api/withdrawals/multi-gateway/request', {
                companyId: companyData.id,
                amount: numericValue
            }, {
                headers: {
                    'Authorization': `Bearer ${authService.getToken()}`
                }
            });

            if (response.data.success) {
                setWithdrawLoading(false);
                setWithdrawModalOpen(false);
                setWithdrawAmount("");

                // Exibindo alerta de sucesso
                setAlertInfo({
                    show: true,
                    type: "success",
                    title: "Saque solicitado com sucesso!",
                    message: "Sua solicitação foi enviada. O valor será creditado em até 1 dia útil após aprovação."
                });

                // Recarregar dados financeiros
                await fetchFinanceData();

                // Esconder alerta após 5 segundos
                setTimeout(() => {
                    setAlertInfo(null);
                }, 5000);
            } else {
                throw new Error(response.data.message || 'Erro ao processar saque');
            }
        } catch (error: any) {
            console.error('Erro ao processar saque:', error);
            setWithdrawLoading(false);

            let errorMessage = "Erro interno do servidor. Tente novamente.";

            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.message) {
                errorMessage = error.message;
            }

            setAlertInfo({
                show: true,
                type: "error",
                title: "Erro ao processar saque",
                message: errorMessage
            });

            // Esconder alerta após 5 segundos
            setTimeout(() => {
                setAlertInfo(null);
            }, 5000);
        }
    };

    // Função para buscar os dados de financeiro e da empresa
    const fetchFinanceData = async () => {
        try {
            setIsLoading(true);

            // Obter o ID da empresa
            const companyId = authService.getCompanyId();

            if (!companyId) {
                console.error("ID da empresa não encontrado");
                alert("Erro: ID da empresa não encontrado. Por favor, faça login novamente.");
                return;
            }

            // Chamada para a API de saldo
            const response = await api.get(`/api/balance/${companyId}`);

            if (response.data && response.data.success) {
                // Extrair os dados da empresa, saldo e taxas da resposta
                const companyInfo = response.data.data.company;
                const balanceInfo = response.data.data.balance;
                const feesInfo = response.data.data.fees || [];

                // Atualizar o estado com os dados de saldo
                setBalanceData(balanceInfo);

                // Atualizar o estado com as taxas
                setFees(feesInfo);

                // Extrair os dados de configurações (settingsData) se existirem
                const settingsData = companyInfo.settingsData ?
                    (typeof companyInfo.settingsData === 'string' ?
                        JSON.parse(companyInfo.settingsData) :
                        companyInfo.settingsData) :
                    {};

                // Atualizar o estado com os dados da empresa
                setCompanyData({
                    id: companyInfo.id || "",
                    companyId: companyInfo.companyId || "",
                    fantasyName: companyInfo.fantasyName || "",
                    legalName: companyInfo.legalName || "",
                    documentType: companyInfo.documentType || "",
                    documentNumber: companyInfo.documentNumber || "",
                    fullAddress: companyInfo.fullAddress || "",
                    street: companyInfo.street || "",
                    number: companyInfo.number || "",
                    complement: companyInfo.complement || "",
                    neighborhood: companyInfo.neighborhood || "",
                    city: companyInfo.city || "",
                    state: companyInfo.state || "",
                    zipCode: companyInfo.zipCode || "",
                    country: companyInfo.country || "Brasil",
                    phone: companyInfo.phone || "",
                    contactEmail: companyInfo.contactEmail || "",
                    pixKey: companyInfo.pixKey,
                    legalRepresentative: settingsData.legalRepresentative || "",
                    legalRepresentativeDocument: settingsData.legalRepresentativeDocument || ""
                });
            } else {
                console.error("Erro ao carregar dados de saldo:", response.data);
                alert("Erro ao carregar os dados financeiros");
            }
        } catch (error) {
            console.error("Erro na requisição:", error);
            alert("Erro, por favor faça login novamente");
        } finally {
            setIsLoading(false);
        }
    };

    // Efeito para buscar os dados financeiros quando o componente for montado
    useEffect(() => {
        fetchFinanceData();
    }, []);

    // Efeito para atualizar a aba ativa com base no parâmetro da URL
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const tabParam = params.get("tab") as TabType | null;

        if (tabParam && ["payouts", "bank", "fees", "identity"].includes(tabParam)) {
            setActiveTab(tabParam);
        } else if (!tabParam) {
            // Se não houver parâmetro, atualiza a URL para incluir o parâmetro padrão
            navigate("?tab=payouts", { replace: true });
        }
    }, [location.search, navigate]);

    // Função para mudar de aba com tipagem correta
    const handleTabChange = (tabId: TabType) => {
        setActiveTab(tabId);
        navigate(`?tab=${tabId}`);
    };

    return (
        <>
            <PageMeta
                title="Sellyo - Financeiro"
                description="Financeiro"
            />
            <div className="grid grid-cols-12 gap-4 md:gap-6">
                <div className="col-span-12">
                    <div className="flex justify-between items-center">
                        <div className="w-auto">
                            <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
                                Financeiro
                            </h3>
                        </div>
                        {hasPermission('financial.withdrawal') && (
                            <div className="w-auto">
                                <Button size="sm" variant="primary" onClick={handleOpenWithdrawModal}>
                                    Efetuar saque
                                </Button>
                            </div>
                        )}
                    </div>
                </div>

                <div className="col-span-12 space-y-6">
                    <FinanceMetrics
                        available={balanceData.available}
                        pending={balanceData.pending}
                        reserved={balanceData.reserved}
                        total={balanceData.total}
                    />

                    {/* Selo BaaS Asaas: saldo e saques são serviços financeiros do Asaas */}
                    <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 dark:border-gray-800 dark:bg-white/[0.03]">
                        <AsaasFinancialNotice
                            variant="auto"
                            align="left"
                            sealWidth={120}
                            notice="Sua conta de pagamento, saldo e saques são serviços financeiros prestados pelo Asaas, instituição de pagamento autorizada pelo Banco Central do Brasil. A Sellyo atua como plataforma de tecnologia."
                            supportLabel="Suporte Asaas:"
                        />
                    </div>

                    {isLoading && (
                        <div className="col-span-12">
                            <p className="text-gray-500 dark:text-gray-400">Carregando...</p>
                        </div>
                    )}

                    {/* Sistema de abas */}
                    <div className="mb-6 border-b border-gray-200 dark:border-gray-800">
                        <ul className="flex flex-wrap -mb-px">
                            <li className="mr-2">
                                <button
                                    onClick={() => handleTabChange("payouts")}
                                    className={`inline-block py-4 px-4 text-sm font-medium ${activeTab === "payouts"
                                        ? "text-blue-600 border-b-2 border-blue-600 dark:text-blue-500 dark:border-blue-500"
                                        : "text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300"
                                        }`}
                                >
                                    Saques
                                </button>
                            </li>
                            <li className="mr-2">
                                <button
                                    onClick={() => handleTabChange("bank")}
                                    className={`inline-block py-4 px-4 text-sm font-medium ${activeTab === "bank"
                                        ? "text-blue-600 border-b-2 border-blue-600 dark:text-blue-500 dark:border-blue-500"
                                        : "text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300"
                                        }`}
                                >
                                    Dados bancários
                                </button>
                            </li>
                            <li className="mr-2">
                                <button
                                    onClick={() => handleTabChange("fees")}
                                    className={`inline-block py-4 px-4 text-sm font-medium ${activeTab === "fees"
                                        ? "text-blue-600 border-b-2 border-blue-600 dark:text-blue-500 dark:border-blue-500"
                                        : "text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300"
                                        }`}
                                >
                                    Taxas e Prazos
                                </button>
                            </li>
                            <li className="mr-2">
                                <button
                                    onClick={() => handleTabChange("identity")}
                                    className={`inline-block py-4 px-4 text-sm font-medium ${activeTab === "identity"
                                        ? "text-blue-600 border-b-2 border-blue-600 dark:text-blue-500 dark:border-blue-500"
                                        : "text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300"
                                        }`}
                                >
                                    Identidade
                                </button>
                            </li>
                        </ul>
                    </div>

                    {/* Conteúdo das abas */}
                    <div className="space-y-6">
                        {activeTab === "payouts" && (
                            <ComponentCard title="Saques">
                                {/* Filtros */}
                                <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="col-span-1">
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Buscar
                                            </label>
                                            <Input
                                                type="text"
                                                placeholder="ID do saque"
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                className="w-full"
                                            />
                                        </div>
                                        <div className="col-span-1">
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Status
                                            </label>
                                            <select
                                                value={statusFilter}
                                                onChange={(e) => setStatusFilter(e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                            >
                                                <option value="">Todos os status</option>
                                                <option value="requested">Solicitado</option>
                                                <option value="processing">Processando</option>
                                                <option value="completed">Concluído</option>
                                                <option value="failed">Falhou</option>
                                                <option value="cancelled">Cancelado</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                {/* Tabela de saques */}
                                <FinanceTable
                                    searchTerm={searchTerm}
                                    statusFilter={statusFilter}
                                    companyId={companyData.id}
                                />
                            </ComponentCard>
                        )}

                        {activeTab === "bank" && (
                            <div className="p-4">
                                <BankDetails
                                    razaoSocial={companyData.legalName}
                                    documentType={companyData.documentType}
                                    documentNumber={companyData.documentNumber}
                                    chavePix={companyData.pixKey || ""}
                                    companyId={companyData.id}
                                    onSuccess={(message) => {
                                        setAlertInfo({
                                            show: true,
                                            type: "success",
                                            title: "Sucesso!",
                                            message: message
                                        });
                                        setTimeout(() => setAlertInfo(null), 5000);
                                        // Recarregar os dados para atualizar a chave PIX
                                        fetchFinanceData();
                                    }}
                                    onError={(error) => {
                                        const errorMessage = error.response?.data?.message || error.message || 'Erro ao salvar chave PIX';
                                        setAlertInfo({
                                            show: true,
                                            type: "error",
                                            title: "Erro!",
                                            message: errorMessage
                                        });
                                        setTimeout(() => setAlertInfo(null), 5000);
                                    }}
                                />
                            </div>
                        )}

                        {activeTab === "fees" && (
                            <div className="p-4">
                                <FeeDetails
                                    documentNumber={companyData.documentNumber}
                                    fees={fees}
                                />
                            </div>
                        )}

                        {activeTab === "identity" && (
                            <KycDocumentsCard
                                hasPendingDocuments={kycStatus?.hasPendingDocuments || false}
                                isKycComplete={kycStatus?.isKycComplete || false}
                                onboardingUrls={kycStatus?.onboardingUrls || []}
                                loading={kycLoading}
                                onRefresh={refetchKyc}
                            />
                        )}
                    </div>
                </div>
            </div>

            {/* Alerta de sucesso/erro */}
            {alertInfo && alertInfo.show && (
                <div className="mt-4 fixed bottom-4 right-4 z-50">
                    <Alert
                        variant={alertInfo.type}
                        title={alertInfo.title}
                        message={alertInfo.message}
                    />
                </div>
            )}

            {/* Modal de saque */}
            <Modal
                isOpen={withdrawModalOpen}
                onClose={handleCloseWithdrawModal}
                size="sm"
                onConfirm={handleWithdraw}
                confirmText="Efetuar Saque"
                cancelText="Cancelar"
                confirmButtonClass={`${balanceData.available <= 0
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-blue-500 hover:bg-blue-600"
                    } text-white`}
                confirmLoading={withdrawLoading}
            >
                <div className="flex flex-col py-4 px-2">
                    <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Realizar saque
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                        O dinheiro cairá na sua conta em até 1 dia útil.
                    </p>

                    {balanceData.available <= 0 && (
                        <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg mb-4">
                            <div className="flex items-start">
                                <div className="flex-shrink-0 mt-0.5">
                                    <AlertCircle className="h-5 w-5 text-red-500" />
                                </div>
                                <div className="ml-3">
                                    <p className="text-sm text-red-700 dark:text-red-300">
                                        Saldo insuficiente para realizar saque!
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="mb-4">
                        <label htmlFor="withdrawAmount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Valor do saque:
                        </label>
                        <div className="w-full flex items-stretch">
                            <div className="flex-1 mr-2">
                                <Input
                                    id="withdrawAmount"
                                    type="text"
                                    placeholder="0,00"
                                    value={withdrawAmount}
                                    onChange={(e) => {
                                        const { formattedValue } = formatCurrency(e.target.value);
                                        setWithdrawAmount(formattedValue);
                                    }}
                                    className="w-full"
                                    disabled={balanceData.available <= 0}
                                />
                            </div>
                            <Button
                                variant="primary"
                                size="sm"
                                disabled={balanceData.available <= 0}
                                onClick={() => {
                                    const { formattedValue } = formatCurrency(balanceData.available.toString());
                                    setWithdrawAmount(formattedValue);
                                }}
                            >
                                Máx
                            </Button>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                            Disponível: {formatCurrencyDisplay(balanceData.available)}
                        </p>
                    </div>

                    <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                        <div className="flex items-start">
                            <div className="flex-shrink-0 mt-0.5">
                                <AlertCircle className="h-5 w-5 text-blue-500" />
                            </div>
                            <div className="ml-3">
                                <p className="text-sm text-gray-700 dark:text-gray-300">É cobrada uma taxa de R$ 3,67 por saque.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    );
}